// Uploads to Firebase Storage.
//
// Generalised out of uploadLogo.ts, which did exactly this for team and project
// logos. Deliberately reuses that bucket and that auth gate rather than adding
// Vercel Blob or S3: the credentials are already in every environment and the
// CORS rules are already configured (firebase-cors.json), so a second provider
// would buy nothing and leave two upload paths to keep working.
//
// Draft assets are video as often as image and much larger than a logo, so the
// size cap and MIME allowlist are per-call rather than global.

import {
  ref,
  uploadBytesResumable,
  getDownloadURL,
  type UploadTaskSnapshot,
} from 'firebase/storage';
import { storage, auth } from './firebase';

export interface UploadProgress {
  progress: number;
  state: 'running' | 'paused' | 'success' | 'error';
}

export interface UploadResult {
  url: string;
  path: string;
}

export type ProgressCallback = (progress: UploadProgress) => void;

export class AssetUploadError extends Error {
  constructor(
    message: string,
    public code: 'INVALID_TYPE' | 'FILE_TOO_LARGE' | 'UPLOAD_FAILED' | 'NOT_AUTHENTICATED'
  ) {
    super(message);
    this.name = 'AssetUploadError';
  }
}

export const IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];

/** What a creator actually delivers: a cut video, or stills for a carousel. */
export const DRAFT_ASSET_TYPES = [
  ...IMAGE_TYPES,
  'video/mp4',
  'video/quicktime',
  'video/webm',
];

export const MB = 1024 * 1024;

export interface UploadOptions {
  /** Defaults to images only. */
  allowedTypes?: string[];
  /** Defaults to 5MB, which is a logo. Drafts pass something much larger. */
  maxBytes?: number;
  onProgress?: ProgressCallback;
}

/**
 * Upload one file under `folder` and return its download URL.
 *
 * The filename is prefixed with a timestamp and stripped of anything outside
 * `[A-Za-z0-9.-]`, so two creators uploading `final.mp4` on the same
 * collaboration do not overwrite each other, and a crafted name cannot walk out
 * of its folder.
 */
export async function uploadAsset(
  file: File,
  folder: string,
  options: UploadOptions = {}
): Promise<UploadResult> {
  const path = await uploadBytes(file, folder, options);

  try {
    return { url: await getDownloadURL(ref(storage, path)), path };
  } catch (error) {
    throw new AssetUploadError(
      `Failed to get download URL: ${(error as Error).message}`,
      'UPLOAD_FAILED'
    );
  }
}

/**
 * Upload and return only the storage path — no download URL.
 *
 * For files whose Storage rules deny client reads, where `getDownloadURL()`
 * would fail. That is the point rather than a limitation: the URL it mints
 * carries a long-lived token, so anyone holding the link reads the file forever
 * without authenticating and the read never reaches our server. Fiscal
 * documents are served instead by the API, which authorizes the reader against
 * a live collaboration, writes an audit row, and signs a URL that expires in
 * minutes.
 */
export async function uploadPrivateAsset(
  file: File,
  folder: string,
  options: UploadOptions = {}
): Promise<{ path: string }> {
  return { path: await uploadBytes(file, folder, options) };
}

/** Shared body of both upload paths. Resolves the stored path. */
async function uploadBytes(
  file: File,
  folder: string,
  options: UploadOptions = {}
): Promise<string> {
  const allowedTypes = options.allowedTypes ?? IMAGE_TYPES;
  const maxBytes = options.maxBytes ?? 5 * MB;

  // Storage rules are the real gate; this is the message the user sees when
  // they are signed out, instead of an opaque permission failure.
  if (!auth.currentUser) {
    throw new AssetUploadError('You must be logged in to upload files', 'NOT_AUTHENTICATED');
  }

  if (!allowedTypes.includes(file.type)) {
    throw new AssetUploadError(
      `Invalid file type. Allowed: ${allowedTypes.map((t) => t.split('/')[1]).join(', ')}`,
      'INVALID_TYPE'
    );
  }

  if (file.size > maxBytes) {
    throw new AssetUploadError(
      `File too large. Maximum size is ${Math.round(maxBytes / MB)}MB`,
      'FILE_TOO_LARGE'
    );
  }

  const safeName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
  const filePath = `${folder}/${Date.now()}_${safeName}`;
  const uploadTask = uploadBytesResumable(ref(storage, filePath), file, {
    contentType: file.type,
  });

  return new Promise((resolve, reject) => {
    uploadTask.on(
      'state_changed',
      (snapshot: UploadTaskSnapshot) => {
        options.onProgress?.({
          progress: (snapshot.bytesTransferred / snapshot.totalBytes) * 100,
          state: snapshot.state as UploadProgress['state'],
        });
      },
      (error) => {
        reject(new AssetUploadError(`Upload failed: ${error.message}`, 'UPLOAD_FAILED'));
      },
      () => resolve(filePath)
    );
  });
}

/** Draft files and published-post proof, kept per collaboration. */
export function uploadCollaborationAsset(
  file: File,
  collaborationId: string,
  kind: 'drafts' | 'proof',
  onProgress?: ProgressCallback
): Promise<UploadResult> {
  return uploadAsset(file, `collaborations/${collaborationId}/${kind}`, {
    allowedTypes: kind === 'drafts' ? DRAFT_ASSET_TYPES : IMAGE_TYPES,
    maxBytes: kind === 'drafts' ? 100 * MB : 10 * MB,
    onProgress,
  });
}

/** A scan or photo of a document. PDFs are what a DGI certificate arrives as. */
export const FISCAL_DOCUMENT_TYPES = [...IMAGE_TYPES, 'application/pdf'];

/**
 * Identity and tax documents.
 *
 * Foldered by **Firebase UID**, not our `User.id`, because that is what
 * `request.auth.uid` carries in `storage.rules` — the write rule compares the
 * two, and `attachFiscalDocument` checks the same prefix server-side. Using our
 * own id here would make the rule compare a value to itself and let anyone write
 * anywhere.
 *
 * Returns a path only. There is no readable URL for these files by design; call
 * the `fiscalDocumentUrl` mutation to get a short-lived one.
 */
export function uploadFiscalDocument(
  file: File,
  firebaseUid: string,
  onProgress?: ProgressCallback
): Promise<{ path: string }> {
  return uploadPrivateAsset(file, `fiscal/${firebaseUid}`, {
    allowedTypes: FISCAL_DOCUMENT_TYPES,
    maxBytes: 10 * MB,
    onProgress,
  });
}
